import { useMemo, memo } from 'react'
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts'

type EmotionData = {
  emotion: string
  confidence: number
}

type BarChartProps = {
  emotions: EmotionData[]
}

const EMOTION_ORDER = ['angry', 'disgusted', 'fearful', 'happy', 'neutral', 'sad', 'surprised']

const EMOTION_NAMES = EMOTION_ORDER.map((e) => e.charAt(0).toUpperCase() + e.slice(1))

const EMOTION_COLORS: Record<string, string> = {
  angry: '#ef4444',
  disgusted: '#8b5cf6',
  fearful: '#6366f1',
  happy: '#22c55e',
  neutral: '#6b7280',
  sad: '#3b82f6',
  surprised: '#f59e0b',
}

function BarChartComponent({ emotions }: BarChartProps) {
  const emotionMap = useMemo(() => {
    const map = new Map<string, number>()
    emotions.forEach((e) => map.set(e.emotion, e.confidence))
    return map
  }, [emotions])

  const data = useMemo(() => {
    return EMOTION_ORDER.map((emotion, index) => {
      const confidence = emotionMap.get(emotion) || 0
      return {
        name: EMOTION_NAMES[index],
        confidence: Number((confidence * 100).toFixed(1)),
        emotion: emotion,
      }
    })
  }, [emotionMap])

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RechartsBarChart 
        data={data} 
        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          key="x-axis-static"
          dataKey="name" 
          stroke="#6b7280" 
          tick={{ fill: '#6b7280' }}
          angle={-45}
          textAnchor="end"
          height={60}
          type="category"
          interval={0}
        />
        <YAxis 
          key="y-axis-static"
          stroke="#6b7280" 
          tick={{ fill: '#6b7280' }} 
          label={{ value: 'Confidence %', angle: -90, position: 'insideLeft' }}
          domain={[0, 100]}
        />
        <Tooltip formatter={(value: number) => `${value}%`} contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
        <Legend />
        <Bar 
          dataKey="confidence" 
          fill="#3b82f6" 
          radius={[8, 8, 0, 0]}
          isAnimationActive={true}
          animationDuration={600}
          animationBegin={0}
          animationEasing="ease-out"
        >
          {data.map((entry) => (
            <Cell key={`cell-${entry.emotion}`} fill={EMOTION_COLORS[entry.emotion] || '#6b7280'} />
          ))}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  )
}

export default memo(BarChartComponent, (prevProps, nextProps) => {
  if (prevProps.emotions.length !== nextProps.emotions.length) {
    return false
  }
  
  const prevMap = new Map(prevProps.emotions.map((e) => [e.emotion, e.confidence]))
  const nextMap = new Map(nextProps.emotions.map((e) => [e.emotion, e.confidence]))
  
  for (const emotion of EMOTION_ORDER) {
    const prevConf = prevMap.get(emotion) || 0
    const nextConf = nextMap.get(emotion) || 0
    if (Math.abs(prevConf - nextConf) > 0.001) {
      return false
    }
  }
  
  return true
})

